import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { homeFor } from "../App";

// Letterhead-style login. Wide left panel carries the firm mark and a
// generous slab of quiet grid rules; right panel is a single quiet card
// with the sign-in form. Kept intentionally sparse — this is the first
// thing prospects and staff see.
export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email.trim(), password);
      navigate(homeFor(u.role));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check credentials.");
    } finally {
      setBusy(false);
    }
  };

  const today = new Date().toLocaleDateString("en-NZ", {
    day: "2-digit", month: "long", year: "numeric",
  });

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[1.15fr_1fr] bg-[var(--paper)]" data-testid="login-page">
      {/* Left: letterhead */}
      <div className="hidden md:flex flex-col justify-between p-16 lg:p-24 border-r border-[var(--rule)] relative overflow-hidden">
        {/* Faint grid rules — like sketchbook paper */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.35] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, var(--rule) 1px, transparent 1px), linear-gradient(to bottom, var(--rule) 1px, transparent 1px)",
            backgroundSize: "96px 96px",
          }}
        />

        <header className="relative z-10 flex items-baseline justify-between">
          <div className="eyebrow">Forecast — {today}</div>
          <div className="eyebrow hidden lg:block">Auckland · Aotearoa</div>
        </header>

        <div className="relative z-10">
          <div className="eyebrow mb-6">The practice</div>
          <h1 className="font-display font-black text-[clamp(3.2rem,7vw,5.5rem)] leading-[0.92] tracked-tight">
            Monk<br />Mackenzie
          </h1>
          <div className="mt-10 max-w-md text-sm text-[var(--muted-fg)] leading-relaxed">
            The firm's live forecast. Projects, principals, capacity, invoicing
            and leave — always up to date with the numbers the practice
            actually enters each month.
          </div>
        </div>

        <footer className="relative z-10 flex items-center justify-between eyebrow">
          <span>Architects</span>
          <span className="font-mono-num normal-case tracking-[0.06em]">est. 2007</span>
        </footer>
      </div>

      {/* Right: quiet form card */}
      <div className="flex items-center justify-center px-6 py-16 md:px-12">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <div className="eyebrow mb-3">Sign in</div>
          <h2 className="h-page mb-2">Open the forecast</h2>
          <div className="t-caption mb-12">Use your firm email — admins see the whole practice, principals see their portfolio.</div>

          <label className="block">
            <span className="eyebrow">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-2 border-b border-[var(--rule-strong)] bg-transparent py-2 text-[var(--ink)] outline-none focus:border-[var(--ink)] font-mono-num transition-colors"
              data-testid="login-email-input"
            />
          </label>

          <label className="block mt-8">
            <span className="eyebrow">Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-2 border-b border-[var(--rule-strong)] bg-transparent py-2 text-[var(--ink)] outline-none focus:border-[var(--ink)] font-mono-num transition-colors"
              data-testid="login-password-input"
            />
          </label>

          {error ? (
            <div
              className="mt-6 text-xs font-mono-num text-[#6B2F2A] inline-flex items-start gap-2"
              data-testid="login-error"
              role="alert"
            >
              <span aria-hidden className="mt-0.5">▲</span>
              <span>{error}</span>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={busy}
            className="mt-10 w-full bg-[var(--ink)] text-white py-3 font-display tracked text-xs uppercase transition-opacity disabled:opacity-40 hover:opacity-90"
            data-testid="login-submit-button"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="mt-8 flex items-center justify-between text-[10px] font-mono-num text-[var(--soft-fg)]">
            <span>© Monk Mackenzie</span>
            <span>Forecasting v2</span>
          </div>
        </form>
      </div>
    </div>
  );
}
